import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/api';
import { useState } from 'react';

type Profile = {
  id: number;
  country_code: string;
  name: string;
  questions: unknown;
  outcomes: unknown;
  disclaimer: string;
  is_active: boolean;
};

export function ComplianceProfilesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['compliance-profiles'],
    queryFn: () => adminFetch<{ profiles: Profile[] }>('/sales/compliance-profiles'),
  });
  const [editing, setEditing] = useState<Profile | null>(null);
  const [form, setForm] = useState({
    name: '',
    disclaimer: '',
    questionsJson: '',
    outcomesJson: '',
    is_active: true,
  });

  const save = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error('Nenhum perfil');
      return adminFetch(`/sales/compliance-profiles/${editing.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: form.name,
          disclaimer: form.disclaimer,
          questions: JSON.parse(form.questionsJson),
          outcomes: JSON.parse(form.outcomesJson),
          is_active: form.is_active,
        }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['compliance-profiles'] });
      setEditing(null);
    },
  });

  if (isLoading) return <p className="text-neutral-400">Carregando…</p>;
  if (error) return <p className="text-red-400">{(error as Error).message}</p>;

  const profiles = data?.profiles ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Compliance por país</h1>
        <p className="text-sm text-neutral-400">
          Textos e resultados do assistente de vendas por cotas
        </p>
      </div>

      <div className="grid gap-3">
        {profiles.map((profile) => (
          <div key={profile.id} className="rounded-xl border border-neutral-800 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium">
                  {profile.country_code} — {profile.name}
                </div>
                <div className="text-xs text-neutral-500">
                  {profile.is_active ? 'Ativo' : 'Inativo'}
                </div>
              </div>
              <button
                type="button"
                className="text-sm text-sky-400"
                onClick={() => {
                  setEditing(profile);
                  setForm({
                    name: profile.name,
                    disclaimer: profile.disclaimer,
                    questionsJson: JSON.stringify(profile.questions, null, 2),
                    outcomesJson: JSON.stringify(profile.outcomes, null, 2),
                    is_active: profile.is_active,
                  });
                }}
              >
                Editar
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="rounded-xl border border-neutral-700 bg-neutral-900 p-4 space-y-3">
          <h2 className="font-medium">Editando {editing.country_code}</h2>
          <input
            className="w-full rounded border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <textarea
            className="w-full rounded border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm"
            rows={3}
            value={form.disclaimer}
            onChange={(e) => setForm((f) => ({ ...f, disclaimer: e.target.value }))}
          />
          <label className="block text-xs text-neutral-400">Questions (JSON)</label>
          <textarea
            className="w-full rounded border border-neutral-700 bg-neutral-950 px-3 py-2 font-mono text-xs"
            rows={10}
            value={form.questionsJson}
            onChange={(e) => setForm((f) => ({ ...f, questionsJson: e.target.value }))}
          />
          <label className="block text-xs text-neutral-400">Outcomes (JSON)</label>
          <textarea
            className="w-full rounded border border-neutral-700 bg-neutral-950 px-3 py-2 font-mono text-xs"
            rows={12}
            value={form.outcomesJson}
            onChange={(e) => setForm((f) => ({ ...f, outcomesJson: e.target.value }))}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
            />
            Ativo
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded bg-sky-600 px-3 py-1.5 text-sm"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              Salvar
            </button>
            <button
              type="button"
              className="rounded border border-neutral-600 px-3 py-1.5 text-sm"
              onClick={() => setEditing(null)}
            >
              Cancelar
            </button>
          </div>
          {save.isError && (
            <p className="text-sm text-red-400">{(save.error as Error).message}</p>
          )}
        </div>
      )}
    </div>
  );
}
